import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { AuthService } from '../auth/auth.service';
import type { BenefitPassDetailVm } from '../benefit-pass/benefit-pass.service';
import type { CartLineVm, CartSummaryVm } from '../cart/cart.service';
import { AccountService } from '../profile/account.service';
import type { ProductDetailVm } from '../products/product-catalog.service';
import type { CatalogProduct } from '../products/product-identity';
import type { WishlistProductVm } from '../wishlist/wishlist.service';
import {
  benefitPassDetailToAnalyticsItem,
  captureAttribution,
  cartLineToAnalyticsItem,
  cartSignature,
  cartToAnalyticsItems,
  classifyCheckoutError,
  classifyLoginError,
  deviceType,
  inferAddToCartSurface,
  inferPasswordPopupContext,
  mapProducts,
  normalizeBuyerStatus,
  normalizeDocumentType,
  normalizeSegment,
  pageViewOf,
  productDetailToAnalyticsItem,
  productToAnalyticsItem,
  roundMoney,
  secondsSince,
  wishlistItemToAnalyticsItem,
} from './data-layer.mappers';
import {
  clearCheckoutStarted,
  clearPendingPurchase,
  hasTrackedPurchase,
  incrementDniRetryCount,
  markCheckoutStarted,
  markTrackedPurchase,
  readAttribution,
  readDniRetryCount,
  readPendingPurchase,
  readSessionStart,
  readUserProperties,
  writeAttribution,
  writePendingPurchase,
  writeUserProperties,
} from './data-layer.storage';
import {
  ANALYTICS_AFFILIATION,
  ANALYTICS_CHANNEL,
  ANALYTICS_CURRENCY,
  type AddToCartSurface,
  type AnalyticsItem,
  type AnalyticsOrderTotals,
  type AnalyticsPendingPurchase,
  type AnalyticsShippingInfo,
  type AnalyticsUserProperties,
  type CheckoutErrorStep,
  type CheckoutErrorType,
  type PasswordPopupDismissMethod,
  type PasswordPopupTriggerContext,
} from './data-layer.types';

type DataLayerEntry = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class DataLayerService {
  private readonly auth = inject(AuthService);
  private readonly account = inject(AccountService);
  private readonly router = inject(Router);
  private sessionStart = Date.now();
  private userProperties: AnalyticsUserProperties | null = readUserProperties();
  private lastPaymentInfoMethod = '';
  private lastViewCartSignature: string | null = null;
  private readonly trackedKeys = new Map<string, null | undefined>();

  bootstrap(): void {
    this.sessionStart = readSessionStart(Date.now());
    const storedAttribution = readAttribution();
    const incoming = captureAttribution();
    if (!storedAttribution || incoming.source || incoming.medium || incoming.campaign) {
      writeAttribution(incoming);
    }
    if (this.auth.isAuthenticated()) {
      void this.hydrateUserProperties('reload');
    }
  }

  async hydrateUserProperties(reason: 'login' | 'reload'): Promise<AnalyticsUserProperties> {
    const sessionId = this.auth.user()?.id ?? '';
    const attribution = readAttribution() ?? captureAttribution();
    let userId = sessionId;
    let segment = this.userProperties?.user_segment ?? '';
    let buyerStatus = this.userProperties?.buyer_status ?? '';
    try {
      const profile = await this.account.getProfile();
      userId = profile.numberCard.trim() || sessionId;
      segment = normalizeSegment(profile.segment?.prefix, profile.numberCard);
      buyerStatus = normalizeBuyerStatus(profile.buyerStatus);
    } catch {
      // Guest profile failures must not block login or page load.
    }
    const properties: AnalyticsUserProperties = {
      user_id: userId,
      user_segment: segment,
      user_channel: ANALYTICS_CHANNEL,
      acquisition_source: attribution.acquisitionSource,
      buyer_status: buyerStatus,
    };
    this.userProperties = properties;
    writeUserProperties(properties);
    if (reason === 'reload' && userId) {
      this.push({
        event: 'user_properties',
        user_id: userId,
        user_segment: segment,
        buyer_status: buyerStatus,
        user_properties: properties,
      });
    }
    return properties;
  }

  loginPageView(): void {
    const attribution = readAttribution() ?? captureAttribution();
    this.push({
      event: 'login_page_view',
      ...pageViewOf(this.router.url),
      source: attribution.source,
      medium: attribution.medium,
      campaign: attribution.campaign,
      session_active: this.auth.isAuthenticated(),
    });
  }

  loginDniFocus(timeOnPageBeforeFocus: number): void {
    this.push({
      event: 'login_dni_focus',
      device_type: deviceType(),
      time_on_page_before_focus: Math.max(0, Math.round(timeOnPageBeforeFocus)),
    });
  }

  loginDniAttempt(documentType: string): void {
    this.push({
      event: 'login_dni_attempt',
      document_type: normalizeDocumentType(documentType),
      device_type: deviceType(),
    });
  }

  async loginDniSuccess(): Promise<void> {
    const properties = await this.hydrateUserProperties('login');
    this.push({
      event: 'login_dni_success',
      user_id: properties.user_id,
      user_segment: properties.user_segment,
      buyer_status: properties.buyer_status,
      session_duration_to_login: secondsSince(this.sessionStart),
      user_properties: properties,
    });
  }

  loginDniError(error: unknown, documentType: string): void {
    this.push({
      event: 'login_dni_error',
      error_type: classifyLoginError(error),
      document_type: normalizeDocumentType(documentType),
      retry_count: incrementDniRetryCount(),
    });
  }

  signupIntent(documentType: string, sourcePage: string): void {
    this.push({
      event: 'signup_intent',
      document_type: normalizeDocumentType(documentType),
      source_page: sourcePage.trim() || pageViewOf(this.router.url).page_path,
    });
  }

  loginRetry(): void {
    this.push({
      event: 'login_retry',
      retry_count: readDniRetryCount(),
    });
  }

  passwordPopupTrigger(context?: PasswordPopupTriggerContext): PasswordPopupTriggerContext {
    const triggerContext = context ?? this.passwordPopupContext();
    this.push({
      event: 'password_popup_trigger',
      trigger_context: triggerContext,
      user_id: this.userId(),
      user_segment: this.userSegment(),
    });
    return triggerContext;
  }

  passwordPopupSuccess(context: PasswordPopupTriggerContext, timeToComplete: number): void {
    this.push({
      event: 'password_popup_success',
      trigger_context: context,
      user_id: this.userId(),
      time_to_complete: Math.max(0, Math.round(timeToComplete)),
    });
    void this.hydrateUserProperties('login');
  }

  passwordPopupDismiss(
    context: PasswordPopupTriggerContext,
    dismissMethod: PasswordPopupDismissMethod,
  ): void {
    this.push({
      event: 'password_popup_dismiss',
      trigger_context: context,
      user_id: this.userId(),
      dismiss_method: dismissMethod,
    });
  }

  passwordPopupContext(): PasswordPopupTriggerContext {
    return inferPasswordPopupContext(this.router.url);
  }

  viewItemList(listId: string, listName: string, products: readonly CatalogProduct[]): void {
    const items = mapProducts(products, { listId, listName });
    this.pushEcommerce('view_item_list', {
      item_list_id: listId,
      item_list_name: listName,
      user_segment: this.userSegment(),
      items,
    });
  }

  selectItem(product: CatalogProduct, listId: string, listName: string, index?: number): void {
    const item = productToAnalyticsItem(product, { listId, listName, index });
    if (!item) return;
    this.pushEcommerce('select_item', {
      item_list_id: listId,
      item_list_name: listName,
      items: [item],
    });
  }

  viewItemFromProduct(product: ProductDetailVm): void {
    const item = productDetailToAnalyticsItem(product);
    if (!item) return;
    this.pushEcommerce('view_item', {
      currency: ANALYTICS_CURRENCY,
      user_segment: this.userSegment(),
      items: [item],
    });
  }

  viewItemFromBenefitPass(detail: BenefitPassDetailVm): void {
    const item = benefitPassDetailToAnalyticsItem(detail);
    if (!item) return;
    this.pushEcommerce('view_item', {
      currency: ANALYTICS_CURRENCY,
      user_segment: this.userSegment(),
      items: [item],
    });
  }

  search(searchTerm: string, resultsCount: number): void {
    const term = searchTerm.trim();
    if (!term) return;
    this.push({
      event: 'search',
      search_term: term,
      results_count: Math.max(0, Math.round(resultsCount)),
      user_segment: this.userSegment(),
    });
  }

  addToWishlistFromProduct(product: ProductDetailVm): void {
    const item = productDetailToAnalyticsItem(product);
    if (!item) return;
    this.pushEcommerce('add_to_wishlist', {
      currency: ANALYTICS_CURRENCY,
      items: [item],
    });
  }

  addToWishlistFromBenefitPass(detail: BenefitPassDetailVm): void {
    const item = benefitPassDetailToAnalyticsItem(detail);
    if (!item) return;
    this.pushEcommerce('add_to_wishlist', {
      currency: ANALYTICS_CURRENCY,
      items: [item],
    });
  }

  removeFromWishlistFromProduct(product: ProductDetailVm): void {
    this.pushRemoveFromWishlist(productDetailToAnalyticsItem(product));
  }

  removeFromWishlistFromBenefitPass(detail: BenefitPassDetailVm): void {
    this.pushRemoveFromWishlist(benefitPassDetailToAnalyticsItem(detail));
  }

  removeFromWishlistItem(item: WishlistProductVm): void {
    this.pushRemoveFromWishlist(wishlistItemToAnalyticsItem(item));
  }

  addProductToCart(product: CatalogProduct, quantity = 1, sourceSurface?: AddToCartSurface): void {
    const item = productToAnalyticsItem(product, {});
    if (!item) return;
    this.pushAddToCart([{ ...item, quantity: Math.max(1, quantity) }], sourceSurface);
  }

  addProductDetailToCart(
    product: ProductDetailVm,
    quantity = 1,
    sourceSurface: AddToCartSurface = 'view_item',
  ): void {
    const item = productDetailToAnalyticsItem(product);
    if (!item) return;
    this.pushAddToCart([{ ...item, quantity: Math.max(1, quantity) }], sourceSurface);
  }

  addBenefitPassToCart(
    detail: BenefitPassDetailVm,
    quantity = 1,
    sourceSurface: AddToCartSurface = 'view_item',
  ): void {
    const item = benefitPassDetailToAnalyticsItem(detail);
    if (!item) return;
    this.pushAddToCart([{ ...item, quantity: Math.max(1, quantity) }], sourceSurface);
  }

  removeCartLine(line: CartLineVm): void {
    const item = cartLineToAnalyticsItem(line);
    if (!item) return;
    const quantity = item.quantity ?? line.quantity;
    this.pushEcommerce('remove_from_cart', {
      currency: ANALYTICS_CURRENCY,
      value: roundMoney(item.price * quantity),
      items: [{ ...item, quantity }],
    });
  }

  viewCart(cart: CartSummaryVm): void {
    const items = cartToAnalyticsItems(cart);
    if (!items.length) return;
    const value = roundMoney(cart.total);
    const signature = cartViewSignature(value, items);
    // Minicart and cart page both fire view_cart for the same cart; send it once until something else happens.
    if (signature === this.lastViewCartSignature) return;
    if (
      this.pushEcommerce('view_cart', {
        currency: ANALYTICS_CURRENCY,
        value,
        user_segment: this.userSegment(),
        items,
      })
    ) {
      this.lastViewCartSignature = signature;
    }
  }

  beginCheckout(cart: CartSummaryVm): void {
    const items = cartToAnalyticsItems(cart);
    if (!items.length || !markCheckoutStarted(cartSignature(cart))) return;
    this.pushEcommerce('begin_checkout', {
      currency: ANALYTICS_CURRENCY,
      value: roundMoney(cart.total),
      user_segment: this.userSegment(),
      items,
    });
  }

  addShippingInfo(cart: CartSummaryVm, shipping: AnalyticsShippingInfo, total?: number): void {
    const items = cartToAnalyticsItems(cart);
    if (!items.length) return;
    this.pushEcommerce('add_shipping_info', {
      currency: ANALYTICS_CURRENCY,
      value: roundMoney(total ?? cart.total),
      shipping_cost: roundMoney(shipping.shippingCost),
      shipping_time: shipping.shippingTime,
      seller: shipping.sellers.filter((seller) => seller.trim()).join(', '),
      items,
    });
  }

  addPaymentInfo(
    cart: CartSummaryVm,
    paymentMethod: string,
    paymentType: string,
    totals: AnalyticsOrderTotals = {},
  ): void {
    const method = paymentMethod.trim();
    const type = paymentType.trim();
    if (!method || !type || this.lastPaymentInfoMethod === method) return;
    const items = cartToAnalyticsItems(cart);
    const value = orderValue(cart, totals);
    if (!items.length || value <= 0) return;
    this.lastPaymentInfoMethod = method;
    this.pushEcommerce('add_payment_info', {
      currency: ANALYTICS_CURRENCY,
      value,
      shipping: roundMoney(totals.shipping ?? 0),
      payment_type: type,
      items,
    });
  }

  addPaymentMethod(paymentType: string, cardBrand?: string): void {
    const method = paymentType.trim();
    if (!method) return;
    this.push({
      event: 'add_payment_method',
      payment_type: method,
      ...(cardBrand?.trim() ? { card_brand: cardBrand.trim() } : {}),
    });
  }

  removePaymentMethod(cardBrand?: string): void {
    this.push({
      event: 'remove_payment_method',
      ...(cardBrand?.trim() ? { card_brand: cardBrand.trim() } : {}),
    });
  }

  placeOrder(cart: CartSummaryVm, paymentType: string, totals: AnalyticsOrderTotals = {}): void {
    const items = cartToAnalyticsItems(cart);
    if (!items.length) return;
    const shipping = roundMoney(totals.shipping ?? 0);
    const value = orderValue(cart, totals);
    const payment_type = paymentType.trim();
    // Snapshot for the return from a redirect gateway, when the cart is already converted to an order.
    if (value > 0) {
      writePendingPurchase({ items, value, shipping, currency: ANALYTICS_CURRENCY, payment_type });
    }
    this.pushEcommerce('place_order', {
      currency: ANALYTICS_CURRENCY,
      value,
      shipping,
      payment_type,
      items,
    });
  }

  purchase(
    transactionId: string,
    cart: CartSummaryVm,
    paymentMethod: string,
    totals: AnalyticsOrderTotals = {},
  ): void {
    const orderId = transactionId.trim();
    if (!orderId || hasTrackedPurchase(orderId)) return;
    const payload = purchasePayload(cart, paymentMethod, totals);
    if (!payload) return;
    markTrackedPurchase(orderId);
    clearCheckoutStarted();
    this.lastPaymentInfoMethod = '';
    this.pushEcommerce('purchase', {
      transaction_id: orderId,
      affiliation: ANALYTICS_AFFILIATION,
      value: payload.value,
      shipping: payload.shipping,
      currency: ANALYTICS_CURRENCY,
      payment_type: payload.paymentType,
      payment_method: payload.paymentType,
      channel: ANALYTICS_CHANNEL,
      user_segment: this.userSegment(),
      items: payload.items,
    });
    clearPendingPurchase();
  }

  checkoutError(
    error: unknown,
    cart: CartSummaryVm,
    options: {
      readonly errorStep?: CheckoutErrorStep;
      readonly errorType?: CheckoutErrorType;
      readonly paymentType?: string;
      readonly errorCode?: string;
      readonly errorMessage?: string;
    } = {},
  ): void {
    const items = cartToAnalyticsItems(cart);
    clearCheckoutStarted();
    this.pushEcommerce(
      'checkout_error',
      {
        currency: ANALYTICS_CURRENCY,
        value: roundMoney(cart.total),
        ...(options.paymentType ? { payment_type: options.paymentType } : {}),
        ...(items.length ? { items } : {}),
      },
      {
        error_step: options.errorStep ?? 'payment',
        error_type: options.errorType ?? classifyCheckoutError(error),
        error_message: sanitizeErrorMessage(options.errorMessage ?? messageOf(error)),
        ...(options.errorCode ? { error_code: options.errorCode } : {}),
      },
    );
  }

  private pushRemoveFromWishlist(item: AnalyticsItem | null): void {
    if (!item) return;
    this.pushEcommerce('remove_from_wishlist', {
      currency: ANALYTICS_CURRENCY,
      items: [item],
    });
  }

  private pushAddToCart(items: AnalyticsItem[], sourceSurface?: AddToCartSurface): void {
    if (items.length === 0) return;
    const item = items[0];
    this.pushEcommerce('add_to_cart', {
      currency: ANALYTICS_CURRENCY,
      value: roundMoney(item.price * (item.quantity ?? 1)),
      source_surface: sourceSurface ?? inferAddToCartSurface(this.router.url),
      user_segment: this.userSegment(),
      items,
    });
  }

  private pushEcommerce(
    event: string,
    ecommerce: Record<string, unknown>,
    extra: Record<string, unknown> = {},
  ): boolean {
    const items = Array.isArray(ecommerce['items']) ? (ecommerce['items'] as AnalyticsItem[]) : [];
    if (event !== 'checkout_error' && items.length === 0) return false;
    const value = ecommerce['value'];
    if (event !== 'checkout_error' && typeof value === 'number' && value <= 0) return false;
    if (event !== 'view_cart') this.lastViewCartSignature = null;
    this.push({ event, ecommerce, ...extra });
    return true;
  }

  private push(entry: DataLayerEntry): void {
    const queue = dataLayerQueue();
    queue.push(this.resetEntry());
    queue.push(entry);
    for (const [key, value] of Object.entries(entry)) {
      if (key === 'event') continue;
      this.trackedKeys.set(key, isNested(value) ? null : undefined);
    }
  }

  private resetEntry(): DataLayerEntry {
    const reset: DataLayerEntry = { ecommerce: null };
    for (const [key, value] of this.trackedKeys) {
      if (key === 'ecommerce') continue;
      reset[key] = value;
    }
    return reset;
  }

  private userId(): string {
    return this.userProperties?.user_id || this.auth.user()?.id || '';
  }

  private userSegment(): AnalyticsUserProperties['user_segment'] {
    return this.userProperties?.user_segment ?? '';
  }
}

function orderValue(cart: CartSummaryVm, totals: AnalyticsOrderTotals): number {
  return roundMoney(totals.total ?? cart.total + (totals.shipping ?? 0));
}

function cartViewSignature(value: number, items: readonly AnalyticsItem[]): string {
  const lines = items.map((item) => `${item.item_id}:${item.quantity ?? ''}:${item.price}`);
  return [String(value), ...lines].join('|');
}

interface PurchasePayload {
  readonly items: AnalyticsItem[];
  readonly value: number;
  readonly shipping: number;
  readonly paymentType: string;
}

/**
 * Live cart first. When the cart is already converted to an order (return from a redirect
 * gateway), fall back to the snapshot saved at place_order.
 */
function purchasePayload(
  cart: CartSummaryVm,
  paymentMethod: string,
  totals: AnalyticsOrderTotals,
): PurchasePayload | null {
  const paymentType = paymentMethod.trim();
  const items = cartToAnalyticsItems(cart);
  if (items.length && cart.total > 0) {
    return {
      items,
      value: orderValue(cart, totals),
      shipping: roundMoney(totals.shipping ?? 0),
      paymentType,
    };
  }
  const pending: AnalyticsPendingPurchase | null = readPendingPurchase();
  if (!pending || pending.items.length === 0 || pending.value <= 0) return null;
  return {
    items: pending.items,
    value: pending.value,
    shipping: pending.shipping,
    paymentType: paymentType || pending.payment_type,
  };
}

function dataLayerQueue(): DataLayerEntry[] {
  const host = globalThis as typeof globalThis & { dataLayer?: DataLayerEntry[] };
  host.dataLayer ??= [];
  return host.dataLayer;
}

function isNested(value: unknown): boolean {
  return typeof value === 'object' && value !== null;
}

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  return '';
}

function sanitizeErrorMessage(message: string): string {
  return message.replace(/\d{6,}/g, '[redacted]').slice(0, 180);
}
